import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm, useWatch} from "react-hook-form";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {DictPO} from "@/model/system";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import {SearchData} from "@/widget/SearchData/SearchData";
import {CurrencyYuanOutlined} from '@mui/icons-material';
import {useCreateInvoice, useQueryInvoice, useUpdateInvoice} from "@/service/invoice.s";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {checkQueryKey, emptyValue, setValues} from "@/helper/method";
import Stack from "@mui/material/Stack";
import isEmpty from "lodash/isEmpty";
import {SearchForeheadVO} from "@/service/forehead.s";
import {CustomerPO} from "@/model/customer";
import {SearchContractVO} from "@/service/contract.s";
import {TitleBar} from "@/widget/TitleBar/TitleBar";
import {isNumber} from "lodash";
import {ProductPO} from "@/model/product";

const validationSchema = z.object({
    customerName: z.string().nonempty("请选择客户名称"),
    customerId: z.number({message: "请选择客户名称"}),
    contractId: z.unknown(),
    remark: z.unknown(),
    contractName: z.unknown(),
    productTypeIds: z.unknown(),
    receivingMethod: z.string({message: "请选择到账方式"}).nonempty({message: "请选择到账方式"}),
    taxNumber: z.unknown(),
    depositBank: z.unknown(),
    instanceId: z.number().default(0),
    executeId: z.unknown(),
    id: z.unknown().default(0),
    depositAccount: z.unknown(),
    mobile: z.string().min(8, {message: "号码至少8位"}).max(15, {message: "号码不能超过11位"}).or(z.literal('')),
    email: z.unknown(),
    depositAddress: z.string().optional(),
    invoiceTitle: z.string({message: "请填写开票抬头"}).nonempty({message: "请填写开票抬头"}),
    productName: z.string({message: "请填写业务内容"}).nonempty({message: "请填写业务内容"}),
    productId: z.unknown(),
    invoiceCompanyId: z.unknown(),
    invoiceCompany: z.string({message: "请选择开票公司"}).nonempty({message: "请选择开票公司"}),
    invoiceMoney: z.number({message: '请填写开票金额'}).max(1000000, "金额不能大于百万"),
    contractMoney: z.string({message: '请填写合同金额'}).nonempty({message: "请填写合同金额"}),
    products: z.any(),
    receivingMethodId: z.unknown(),
    invoiceTypeId: z.unknown(),
    invoiceType: z.string({message: '请选择开票类型'}).nonempty({message: '请选择开票类型'}),
}).superRefine((values, ctx) => {
    if (values.invoiceTypeId == 1 && isEmpty(values.mobile)) {
        ctx.addIssue(
            {code: z.ZodIssueCode.custom, message: "请填写联系电话", path: ['mobile']}
        )
    }
});
export type WriteInvoiceForm = z.infer<typeof validationSchema>
const defaultValues = emptyValue(validationSchema)

export const WriteInvoice = () => {
    const allDict = useAllDict()
    const {invoiceType, receivingMethod, company} = allDict
    const {setToast} = useGlobalActions()
    const {onSearch, id: drawerId, write, execute} = useDrawerModal()
    const {mutateAsync: createInvoiceMutate, isPending: createInvoicePending} = useCreateInvoice()
    const {mutateAsync: updateInvoiceMutate, isPending: updateInvoicePending} = useUpdateInvoice()
    const {data: invoiceData} = useQueryInvoice({id: drawerId!}, isNumber(drawerId))
    const invoice = invoiceData?.data
    const {setDrawerModal} = useGlobalActions()

    const initValues = () => {
        if (write == "edit") {
            return {...invoice, invoiceMoney: parseFloat(invoice?.invoiceMoney || "")}
        }
        return {}
    }

    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        watch,
        formState: {errors},
    } = useForm<WriteInvoiceForm>({
        resolver: zodResolver(validationSchema),
        values: initValues() as WriteInvoiceForm,
        defaultValues,
    });

    const onSubmit = async (values: WriteInvoiceForm) => {
        const result = write == 'add' ? await createInvoiceMutate(values) : await updateInvoiceMutate({...values, ...execute, id: drawerId})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    const [customerId] = watch(["customerId", 'contractId', 'productTypeIds'])

    const BusinessWatched = () => {
        const products: ProductPO[] = useWatch({
            name: 'products',
            control
        })
        return <Grid size={6}>
            <FormControl fullWidth={true} required={true}>
                <FormLabel>业务内容</FormLabel>
                <Controller control={control} render={({field: {onChange, value}}) => {
                    return <Autocomplete
                        options={products && products?.map((item) => ({label: item.category, value: item.id})) || []}
                        value={value as any}
                        inputValue={value as any}
                        onChange={((event, option: DictPO | null) => {
                            setValue("productId", option?.value)
                            onChange(option?.label)
                        })}
                        filterSelectedOptions={false}
                        freeSolo={true}
                        fullWidth
                        renderInput={(params) => <TextField
                            error={!!errors?.productName}
                            helperText={errors?.productName?.message}
                            onChange={event => onChange(event.target.value)} placeholder={'请选择业务内容'}
                            {...params}/>}
                    />
                }} name={"productName"}/>
            </FormControl>
        </Grid>
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={1.5} direction={"column"}>
                {/*<TitleBar title={"签单明细"}/>*/}
                <Grid container spacing={3}>
                    <Grid size={6}>
                        <SearchData<WriteInvoiceForm, CustomerPO> required={true} onSelect={() => {
                            setValues<Pick<WriteInvoiceForm, "contractId" | "contractName" | "productName" | "productId" | "contractMoney" | "products">>({
                                contractId: undefined,
                                contractName: '',
                                productName: '',
                                productId: '',
                                contractMoney: '',
                                products: [],
                            }, setValue)
                        }} control={control} attr={"customerName"} attrId={"customerId"} errMsg={errors.customerId?.message}
                                                                  setValue={setValue}
                                                                  label={"客户名称"}/>
                    </Grid>
                    <Grid size={6}>
                        <SearchData<WriteInvoiceForm, SearchContractVO> readOnly={!checkQueryKey(customerId)} onSelect={(item: SearchContractVO) => {
                            setValues<Pick<WriteInvoiceForm, "productName" | "productId" | "productTypeIds" | "contractMoney" | "products">>({
                                productName: '',
                                productId: '',
                                productTypeIds: item?.productTypeIds,
                                contractMoney: item?.totalPrice,
                                products: item?.products || []
                            }, setValue)
                        }} autoLoad={true} query={{customerId}} control={control} tag={2} attr={"contractName"} attrId={"contractId"} setValue={setValue}
                                                                        label={"关联合同"}/>
                    </Grid>
                    <BusinessWatched/>
                    <Grid size={6}>
                        <FormControl fullWidth={true} autoFocus={true} required={true}>
                            <FormLabel>合同金额</FormLabel>
                            <TextField
                                {...register("contractMoney")}
                                error={!!errors?.contractMoney}
                                helperText={errors?.contractMoney?.message}
                                slotProps={{
                                    input: {
                                        endAdornment: <CurrencyYuanOutlined color="disabled"/>,
                                    },
                                }}
                                placeholder="填写合同金额"
                                variant="outlined"
                            /> </FormControl>
                    </Grid>
                </Grid>
                <TitleBar title={"开票信息"}/>
                <Grid container spacing={3}>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>开票类型</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                                options={invoiceType}
                                value={value as any}
                                onChange={((event, option: DictPO | null) => {
                                    setValue("invoiceTypeId", option?.value)
                                    onChange(option?.label)
                                })}
                                fullWidth
                                renderInput={(params) => <TextField placeholder={'请选择开票类型'}
                                                                    error={!!errors?.invoiceType}
                                                                    helperText={errors?.invoiceType?.message} {...params}/>}
                            />} name={"invoiceType"}/>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} autoFocus={true} required={true}>
                            <FormLabel htmlFor="invoiceMoney">开票金额</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <TextField
                                error={!!errors?.invoiceMoney}
                                helperText={errors?.invoiceMoney?.message}
                                id="invoiceMoney"
                                type={'number'}
                                value={value}
                                onChange={event => onChange(parseInt(event.target.value || ''))}
                                slotProps={{
                                    input: {
                                        inputProps: {
                                            min: 0,
                                            max: 1000000,
                                            maxLength: 8, inputMode: 'numeric', pattern: '[0-9]*'
                                        },
                                        endAdornment: <CurrencyYuanOutlined color="disabled"/>,
                                    },
                                }}
                                placeholder="填写开票金额"
                                variant="outlined"
                            />} name={'invoiceMoney'}/>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>开票公司</FormLabel>
                            <Controller name={"invoiceCompany"} control={control} render={({field: {onChange, value}}) => <Autocomplete
                                value={value as any}
                                onChange={(event, option: DictPO | null) => {
                                    setValue("invoiceCompanyId", option?.value)
                                    onChange(option?.label)
                                }}
                                options={company}
                                fullWidth
                                renderInput={(params) => <TextField placeholder={'请选择开票公司'} error={!!errors?.invoiceCompany}
                                                                    helperText={errors?.invoiceCompany?.message} {...params}/>}
                            />}/>
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>到账方式</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                                options={receivingMethod}
                                value={value as any}
                                onChange={(_, option: DictPO | null) => {
                                    setValue("receivingMethodId", option?.value)
                                    onChange(option?.label)
                                }}
                                fullWidth
                                renderInput={(params) => (
                                    <TextField error={!!errors?.receivingMethod}
                                               helperText={errors?.receivingMethod?.message}
                                               variant="outlined"
                                               placeholder={"选择到账方式"} {...params} />
                                )}
                            />} name={"receivingMethod"}/>
                        </FormControl>
                    </Grid>
                    <Grid size={12}>
                        <FormControl fullWidth={true}>
                            <FormLabel htmlFor="remark">备注</FormLabel>
                            <TextField
                                id="remark"
                                multiline={true}
                                rows={3}
                                {...register("remark")}
                                slotProps={{
                                    input: {
                                        sx: {minHeight: 80},
                                        inputProps: {maxLength: 5000},
                                    },
                                }}
                                placeholder="填写备注"
                                variant="outlined"
                            />
                        </FormControl>
                    </Grid>
                </Grid>
                <TitleBar title={"抬头信息"}/>
                <Grid container spacing={3}>
                    <Grid size={6}>
                        <SearchData<WriteInvoiceForm, SearchForeheadVO> control={control} attr={"forehead"} tag={4} attrId={"foreheadId"} onSelect={({remark, ...rest}) => {
                            setValues<any>(rest, setValue as any)
                        }} setValue={setValue} label={"关联抬头"}/>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} autoFocus={true} required={true}>
                            <FormLabel htmlFor="invoiceTitle">开票抬头
                            </FormLabel>
                            <TextField
                                id="invoiceTitle"
                                {...register("invoiceTitle")}
                                slotProps={{
                                    input: {
                                        inputProps: {maxLength: 250},
                                    },
                                }}
                                error={!!errors.invoiceTitle}
                                helperText={errors?.invoiceTitle?.message}
                                placeholder="填写开票抬头"
                                variant="outlined"
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel htmlFor="taxNumber">纳税识别号
                            </FormLabel>
                            <TextField
                                id="taxNumber"
                                {...register("taxNumber")}
                                slotProps={{
                                    input: {
                                        inputProps: {maxLength: 250},
                                    },
                                }}
                                placeholder="填写纳税识别号"
                                variant="outlined"
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel htmlFor="depositBank">开户行
                            </FormLabel>
                            <TextField
                                id="depositBank"
                                {...register("depositBank")}
                                slotProps={{
                                    input: {
                                        inputProps: {maxLength: 250},
                                    },
                                }}
                                placeholder="填写开户行"
                                variant="outlined"
                            />
                        </FormControl>
                    </Grid>

                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel htmlFor="depositAccount">开户账户
                            </FormLabel>
                            <TextField
                                id="depositAccount"
                                {...register("depositAccount")}
                                slotProps={{
                                    input: {
                                        inputProps: {maxLength: 250},
                                    },
                                }}
                                placeholder="填写开户账户"
                                variant="outlined"
                            />
                        </FormControl>
                    </Grid>

                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel htmlFor="mobile">联系电话
                            </FormLabel>
                            <TextField
                                id="mobile"
                                {...register("mobile")}
                                error={!!errors.mobile}
                                helperText={errors.mobile?.message}
                                slotProps={{
                                    input: {
                                        inputProps: {maxLength: 15},
                                    },
                                }}
                                placeholder="填写联系电话"
                                variant="outlined"
                            />
                        </FormControl>
                    </Grid>

                    <Grid size={6}>
                        <FormControl fullWidth={true}>
                            <FormLabel htmlFor="email">电子邮箱</FormLabel>
                            <TextField
                                id="email"
                                {...register("email")}
                                slotProps={{
                                    input: {
                                        inputProps: {maxLength: 250},
                                    },
                                }}

                                placeholder="填写电子邮箱"
                                variant="outlined"
                            />
                        </FormControl>
                    </Grid>
                    <Grid size={6}>
                        <FormControl fullWidth={true}>
                            <FormLabel htmlFor="depositAddress">开票地址
                            </FormLabel>
                            <TextField
                                id="depositAddress"
                                {...register("depositAddress")}
                                multiline={true}
                                slotProps={{
                                    input: {
                                        sx: {height: 'auto'},
                                        inputProps: {maxLength: 250},
                                    },
                                }}
                                placeholder="填写开票地址"
                                variant="outlined"
                            />
                        </FormControl>
                    </Grid>
                </Grid>
                <DrawerFooter loading={createInvoicePending || updateInvoicePending} onReset={() => reset(defaultValues)}/>
            </Stack>
        </Box>
    );
};
